import React, { useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import {
  FileUpload,
  useUploadFileAndExtractMarkdown,
} from "./useUploadFileAndExtractMarkdown";
import { ChatAttachmentItem } from "@src/features/aiAssist/uiComponents/ChatAttachment";
import {
  ACCEPTED_FILE_TYPES,
  ACCEPTED_FILE_TYPES_GROUPED,
  validateFileType,
} from "@src/lib/utils/files";
import { useToast } from "@src/lib/hooks/useToast";

export interface UseAiChatComposerFileUploadProps {
  onAttachmentsChanged?: React.Dispatch<
    React.SetStateAction<ChatAttachmentItem[]>
  >;
  enableDragAndDrop?: boolean;
}

export const useAiChatComposerFileUpload = ({
  onAttachmentsChanged,
  enableDragAndDrop = true,
}: UseAiChatComposerFileUploadProps): {
  triggerFileUpload: () => void;
  HiddenFileInput: React.ReactNode;
  dropzoneProps?: {
    getRootProps: () => React.HTMLAttributes<HTMLDivElement>;
    isDragActive: boolean;
  };
} => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addErrorToast } = useToast();

  const onUploadStarted = useCallback(
    (fileUpload: FileUpload) => {
      if (!onAttachmentsChanged) return;
      const newAttachment = { uploadingFile: fileUpload };
      onAttachmentsChanged((prev) => [newAttachment, ...(prev ?? [])]);
    },
    [onAttachmentsChanged],
  );

  const onUploadCompleted = useCallback(
    (fileUpload: FileUpload) => {
      if (!onAttachmentsChanged) return;
      onAttachmentsChanged((prev) =>
        prev.map((att) => {
          if (att.uploadingFile?.uuid === fileUpload.uuid) {
            return {
              ...att,
              uploadingFile: fileUpload,
              mediaFragment: fileUpload.media,
            };
          }
          return att;
        }),
      );
    },
    [onAttachmentsChanged],
  );

  const onUploadError = useCallback(
    (fileUpload: FileUpload) => {
      if (!onAttachmentsChanged) return;
      onAttachmentsChanged((prev) =>
        prev.map((att) => {
          if (att.uploadingFile?.uuid === fileUpload.uuid) {
            return {
              ...att,
              uploadingFile: fileUpload,
              mediaFragment: fileUpload.media,
            };
          }
          return att;
        }),
      );
    },
    [onAttachmentsChanged],
  );

  const { uploadFiles } = useUploadFileAndExtractMarkdown({
    onUploadStarted,
    onUploadCompleted,
    onUploadError,
  });

  const handleFileRejection = useCallback(() => {
    if (!enableDragAndDrop) return;

    addErrorToast({
      message: "Unsupported file type. Upload a different file.",
      callsite: "ai_chat_composer_file_upload",
    });
  }, [addErrorToast, enableDragAndDrop]);

  const handleFileValidationAndUpload = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;

      const validFiles = files.filter(validateFileType);
      const rejectedFiles = files.filter((file) => !validateFileType(file));

      if (rejectedFiles.length > 0) {
        handleFileRejection();
      }

      if (validFiles.length > 0) {
        uploadFiles(validFiles);
      }
    },
    [uploadFiles, handleFileRejection],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    handleFileValidationAndUpload(Array.from(files));

    // Reset file input to allow selecting the same file again
    if (e.target) {
      e.target.value = "";
    }
  };

  const triggerFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const HiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      accept={ACCEPTED_FILE_TYPES.toString()}
      style={{ display: "none" }}
      onChange={handleFileSelect}
    />
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0 && enableDragAndDrop) {
        handleFileValidationAndUpload(acceptedFiles);
      }
    },
    [enableDragAndDrop, handleFileValidationAndUpload],
  );

  const dropzone = useDropzone({
    useFsAccessApi: true,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDrop,
    onDropRejected: handleFileRejection,
    accept: ACCEPTED_FILE_TYPES_GROUPED,
    disabled: !enableDragAndDrop,
  });

  return {
    triggerFileUpload,
    HiddenFileInput,
    ...(enableDragAndDrop && {
      dropzoneProps: {
        getRootProps: dropzone.getRootProps,
        isDragActive: dropzone.isDragActive,
      },
    }),
  };
};
